import React from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Skeleton, Typography } from '@mui/material';
import { TimesheetSummary } from '../types';
import { percent } from '../utils';

interface Props {
  summary: TimesheetSummary | null;
  error: string | null;
  onRetry: () => void;
}

const BORDER = '#e2e8f0';

const Stat: React.FC<{ label: string; value: number | null; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>{label}</Typography>
    {value === null
      ? <Skeleton width={56} height={32} />
      : <Typography sx={{ fontSize: 24, lineHeight: 1.25, fontWeight: 700, color: color || '#0f172a' }}>{value.toLocaleString('ru-RU')}</Typography>}
  </Box>
);

/** Сводка по аптекам зоны оператора: сколько всего, у скольких есть табель текущего года, сколько осталось создать */
const TimesheetSummaryBar: React.FC<Props> = ({ summary, error, onRetry }) => {
  if (error && !summary) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
        {error}
      </Alert>
    );
  }

  const done = summary ? percent(summary.created, summary.total) : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER, borderRadius: 2, bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: { xs: 2, sm: 5 } }}>
        <Stat label="Аптек в вашей зоне" value={summary ? summary.total : null} />
        <Stat label="Табель создан" value={summary ? summary.created : null} color="#15803d" />
        <Stat label="Табеля нет" value={summary ? summary.missing : null} color={summary && summary.missing > 0 ? '#b45309' : undefined} />
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
              {summary ? `Табели ${summary.year} года` : 'Табели'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>{summary ? `${done}%` : ''}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={done} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a', borderRadius: 4 } }} />
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(TimesheetSummaryBar);
